import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  startTransition,
} from "react"
import styled from "styled-components"
import gsap from "gsap"
import { SplitText } from "gsap/SplitText"
import colors from "assets/styles/colors"
import text, { strokeText } from "assets/styles/text"
import media from "assets/styles/media"
import BGGrid, { LineConfig } from "components/uIElements/BGGrid"
import { ScreenContext } from "components/Providers"
import { ReactComponent as BigQuotesSVG } from "assets/images/homepage/bigQuotes.svg"
import { useMedia } from "utils/hooks"
import { shuffle } from "utils/functions"
import PixelButton from "components/buttons/PixelButton"

gsap.registerPlugin(SplitText)

type QuoteArray = {
  name: string
  job: string
  quote: {
    raw: string
    rawMoblie?: string | undefined
    desktop: string[]
    tablet: string[]
    mobile: string[]
  }
}

const quoteArray = [
  {
    name: "Barry Silbert",
    job: "CEO of Digital Currency Group",
    quote: {
      raw: "Jim is a globally recognized blockchain thought-leader and acted early and with conviction in the space. We are very lucky to have Jim involved with us and being such a big global advocate for both our company and cryptocurrencies generally.",
      desktop: [
        "Jim",
        "thought-leader",
        "conviction",
        "to have Jim",
        "such a big",
        "company",
      ],
      tablet: [
        "Jim",
        "blockchain",
        "acted early",
        "the space.",
        "Jim involved",
        "a big global",
        "company and",
        "generally",
      ],
      mobile: [
        "Jim",
        "blockchain",
        "and acted",
        "conviction",
        "are very lucky",
        "involved",
        "such a big",
        "for both",
        "cryptocurrencies",
      ],
    },
  },
  {
    name: "Rodger Desai",
    job: "CEO of Prove",
    quote: {
      raw: "I’ve known Stu for over 20 years and he has consistently been the most relied upon board member and investment executive for me, our management team and overall board. Much of our success at Prove can be attributed to Stu Ellman and the RRE team.",
      rawMobile:
        "I’ve known Stu for over 20 years and he has consistently been the most relied upon board member and investment executive for our management team and overall board. Much of our success at Prove can be attributed to Stu Ellman and the RRE team.",
      desktop: [
        "I've known",
        "has consistently",
        "board member and",
        "me, our",
        "board.",
        "attributed",
      ],
      tablet: [
        "I've known",
        "he has consistently",
        "relied",
        "investment executive",
        "management",
        "Much of our",
        "attributed to Stu",
        "RRE team",
      ],
      mobile: [
        "I've known",
        "years and",
        "consistently",
        "relied",
        "and investment",
        "for our",
        "and overall",
        "our success",
        "be attributed",
        "and the RRE team",
      ],
    },
  },
  {
    name: "Jonah Peretti",
    job: "Founder and CEO of BuzzFeed",
    quote: {
      raw: `Will and RRE have been great partners. They have always understood BuzzFeed’s vision for a new global media company and they’ve always put the company first. They’ve been a steady, constructive voice since BuzzFeed’s earliest days.`,
      desktop: [
        "Will and RRE",
        "have always understood",
        "for a new global",
        "always put",
        "steady, constructive voice",
        "earliest",
      ],
      tablet: [
        "Will and RRE",
        "partners",
        "understood",
        "new global media",
        "they",
        "company first",
        "a steady",
        "since",
      ],
      mobile: [
        "Will and RRE",
        "great partners",
        "always understood",
        "BuzzFeed",
        "global",
        "and they",
        "company first",
        "a steady",
        "since BuzzFeed",
        "earliest days",
      ],
    },
  },
  {
    name: "Luke Bonney",
    job: "CEO of Redox",
    quote: {
      raw: `Raju is always my first call. He’s driven meaningful and differentiated value at the board level. His operational insights coming from decades of experience are unparalleled in the VCs I have worked with.`,
      desktop: [
        "Raju",
        "meaningful",
        "at the board",
        "coming from decades",
        "unparalleled",
      ],
      tablet: [
        "Raju",
        "driven",
        "value",
        "operational",
        "decades of experience",
        "unparalleled",
        "worked with",
      ],
      mobile: [
        "Raju",
        "He",
        "differentiated",
        "board level",
        "insights",
        "decades of",
        "unparalleled",
        "I have worked",
      ],
    },
  },
  {
    name: "Al Goldstein",
    job: "Co-Founder & Executive Chairman, Avant & Amount",
    quote: {
      raw: `Jim Robinson III has been a great friend, mentor, and investor. It's a true privilege to work with someone who has been on the front lines across so many of the top businesses in the world and deeply understands the challenges of building companies.`,
      desktop: [
        "Jim Robinson",
        "mentor, and",
        "work with",
        "lines",
        "in the world",
        "challenges",
      ],
      tablet: [
        "Jim Robinson",
        "friend",
        "true privilege",
        "who has been",
        "across",
        "in the world",
        "the challenges",
      ],
      mobile: [
        "Jim Robinson",
        "great friend",
        "investor.",
        "to work with",
        "has been on",
        "across",
        "businesses",
        "and deeply",
        "the challenges",
        "building",
      ],
    },
  },
  {
    name: "Luke Schoenfelder",
    job: "CEO of Latch",
    quote: {
      raw: `From the very early days, Jason’s ability to see Latch not as a lock company but as a platform for the future of smart spaces set him apart from the crowd of VCs. He was the driving force behind my decision to partner with RRE in our early days of building Latch.`,
      desktop: [
        "From the very",
        "see Latch",
        "platform",
        "him apart",
        "driving force",
        "with RRE",
      ],
      tablet: [
        "From the very",
        "ability to see",
        "company but",
        "future",
        "apart",
        "was the driving",
        "decision to",
        "early days of",
      ],
      mobile: [
        "From the very",
        "Jason",
        "not as a",
        "as a platform",
        "of smart",
        "apart from the crowd",
        "VC",
        "force behind",
        "partner with RRE",
      ],
    },
  },
]

const Quotes: React.FC<{}> = () => {
  const [isActive, setIsActive] = useState<number>(-1)
  const lastActive = useRef<number>(0)
  const { mobile } = useContext(ScreenContext)
  const right = useRef<HTMLDivElement>(null)
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const firstPass = useRef<boolean>(true)
  const first = useRef<boolean>(true)
  const animating = useRef(false)
  const [renderedQuotes, setRenderedQuotes] = useState<any[]>([])
  const [renderedNums, setRenderedNums] = useState<any[]>([])
  const mediaKeys = useMedia("desktop", "desktop", "tablet", "mobile")

  const cutQuotes = useCallback(
    (breaks: string[], index: number, quote: string) => {
      const newString = quote
      const newArr: JSX.Element[] = []

      breaks.forEach((breakString: string, i: number, arr) => {
        const order = arr.length - i

        if (i === breaks.length - 1) {
          newArr.push(
            <QuoteLine
              order={i}
              reverseOrder={order}
              key={`quote${index}-${i}`}
              className={`quote-${index} quote${index}-${i}`}
            >
              {newString.substring(newString.indexOf(breakString))}
            </QuoteLine>
          )
        } else {
          newArr.push(
            <QuoteLine
              order={i}
              reverseOrder={order}
              key={`quote-${index}-${i}`}
              className={`quote-${index} quote-${index}-${i}`}
            >
              {newString.substring(
                newString.indexOf(breakString),
                newString.indexOf(breaks[i + 1])
              )}
            </QuoteLine>
          )
        }
      })

      return newArr
    },
    [quoteArray]
  )

  useEffect(() => {
    if (!first.current && !animating.current && wrapper) {
      animating.current = true
      const lastActiveCard = lastActive.current
      lastActive.current = isActive

      const tl = gsap.timeline({
        onComplete: () => {
          animating.current = false
        },
      })

      const lastQuoteLines = document.querySelectorAll(
        `.quote-${lastActiveCard}`
      )

      startTransition(() => {
        tl.to(
          `.quote-${isActive}`,
          {
            x: 0,
            y: 0,
            stagger: 0.05,
            duration: 1,
            ease: "customOne",
            transformOrigin: "0% 0%",
            opacity: 1,
            onComplete: () => {
              lastQuoteLines.forEach((line, i) => {
                gsap.set(line, {
                  x: -50,
                  y: 100,

                  transformOrigin: "0% 0%",
                  opacity: 0,
                })
              })
            },
          },
          0.2
        )
          .to(`.counter_number-${isActive}`, { y: 0, duration: 1 }, 0)
          .to(
            `.Quotes__Quote-Job-${isActive}`,
            {
              y: "-=110%",
              duration: 0.5,
              stagger: 0.07,
            },
            0.8
          )
          .to(
            `.Quotes__Quote-Name-${isActive}`,
            {
              y: "-=110%",
              duration: 0.5,
              stagger: 0.07,
              ease: "customOne",
            },
            0.8
          )
          .to(
            `.Quotes__Quote-Job-${lastActiveCard}, .Quotes__Quote-Name-${lastActiveCard}`,
            {
              opacity: 0,
              duration: 0.2,
              stagger: 0.07,
              ease: "customOne",
              onComplete: () => {
                gsap.set(
                  `.Quotes__Quote-Job-${lastActiveCard}, .Quotes__Quote-Name-${lastActiveCard}`,
                  { y: "110%", opacity: 1 }
                )
              },
            },
            0
          )
          .to(
            `.counter_number-${lastActiveCard}`,
            {
              y: firstPass.current ? "110%" : "-110%",
              duration: 1,
              onComplete: () => {
                firstPass.current = false
                gsap.set(`.counter_number-${lastActiveCard}`, { y: "110%" })
              },
            },
            0
          )
          .to(
            `.quote-${lastActiveCard}`,
            {
              opacity: 0,
              x: "+=3.47vw",
              stagger: 0.05,
              ease: "customOne",
              y: -100,
              transformOrigin: "100% 100%",
              duration: 0.7,
            },
            0
          )
      })
    }
  }, [isActive, wrapper])

  useEffect(() => {
    if (wrapper) {
      const tl = gsap.timeline({
        scrollTrigger: {
          scroller: "#___gatsby",
          trigger: "#Quotes",
          start: useMedia("top 50%", "top 50%", "top 80%", "top 80%"),
          end: "bottom bottom",
        },
      })

      tl.to("#Quotes", {
        opacity: 1,
        duration: 0.01,
        onComplete: () => {
          lastActive.current = 1
          first.current = false
          setIsActive(0)
        },
      }, 0)
    }
  }, [wrapper])

  useEffect(() => {
    if (right.current) {
      const tween = gsap.from(right.current, {
        y: "150px",
        backgroundColor: "red",
        scrollTrigger: {
          scroller: "#___gatsby",
          trigger: wrapper,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      })
    }
  }, [right])

  useEffect(() => {
    if (quoteArray && wrapper) {
      const allQuotes = shuffle(quoteArray).map((quote, i, arr) => {
        return (
          <Quote key={`quotes_section${i}`}>
            <QuoteText className={`Quotes__Quote-Text-${i}`}>
              {cutQuotes(
                quote.quote[mediaKeys],
                i,
                mobile && quote.quote.rawMobile
                  ? quote.quote.rawMobile
                  : quote.quote.raw
              )}
            </QuoteText>
            <Job>
              <p className={`Quotes__Quote-Job-${i}`}>{quote.job}</p>
            </Job>
            <Name>
              <p className={`Quotes__Quote-Name-${i}`}> {quote.name}</p>
            </Name>
          </Quote>
        )
      })

      setRenderedQuotes(allQuotes)
    }
  }, [quoteArray, wrapper])

  const handleNext = () => {
    if (isActive < quoteArray.length - 1 && !animating.current) {
      setIsActive(isActive + 1)
    }
  }

  const handlePrevious = () => {
    if (isActive > 0 && !animating.current) {
      setIsActive(isActive - 1)
    }
  }

  useEffect(() => {
    if (quoteArray && wrapper) {
      const counterNumbers = quoteArray.map((num, i) => {
        return (
          <p className={`counter_number-${i}`} key={`counter_number${i}`}>
            {i + 1}
          </p>
        )
      })
      setRenderedNums(counterNumbers)
    }
  }, [quoteArray, wrapper])

  const lineConfig: LineConfig = {
    sectionHeight: "100%",
    animationClass: "quotes",
    sectionRef: wrapper,
    filledColumns: [
      {
        column: mobile ? 0 : 7,
        height: mobile ? "24.533vw" : "16.67vw",
        topOffset: mobile ? "0" : "59.93vw",
        color: colors.lightBlueGradient,
        ref: right,
      },
    ],
  }

  return (
    <Wrapper
      ref={ref => setWrapper(ref)}
      id="Quotes"
      data-scroll
      data-scroll-section
    >
      <BGGrid lineConfig={lineConfig} />
      <BigQuotes />
      <ContentWrapper>
        <Controls>
          {!mobile && (
            <Counter>
              0<Ticker>{renderedNums}</Ticker>/0{renderedNums.length}
            </Counter>
          )}

          <MobileWrapper>
            <PixelButton
              left={true}
              ariaLabel="last quote button"
              onClick={handlePrevious}
              disabled={isActive === 0}
              pixelsToChange={[
                { from: [7, 0], to: [7, -1] },
                { from: [8, 1], to: [8, -1] },
                { from: [9, 0], to: [9, -2] },
              ]}
              color={colors.blue400}
              activeColor={colors.blue500}
              icon={
                <svg
                  viewBox="0 0 12 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  height={"50%"}
                >
                  <path
                    d="M1.04492 21.0448L11.0449 11.0448L1.04492 1.0448"
                    stroke="#ffffff"
                    strokeWidth="1.00224"
                    strokeLinecap="square"
                  />
                </svg>
              }
            ></PixelButton>
            {mobile && (
              <Counter>
                0<Ticker>{renderedNums}</Ticker>/0{renderedNums.length}
              </Counter>
            )}

            <PixelButton
              disabled={isActive === quoteArray.length - 1}
              onClick={handleNext}
              color={colors.blue400}
              activeColor={colors.blue500}
              ariaLabel="next quote button"
              icon={
                <svg
                  viewBox="0 0 12 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  height={"50%"}
                >
                  <path
                    d="M1.04492 21.0448L11.0449 11.0448L1.04492 1.0448"
                    stroke="#ffffff"
                    strokeWidth="1.00224"
                    strokeLinecap="square"
                  />
                </svg>
              }
            />
          </MobileWrapper>
        </Controls>

        {renderedQuotes}
      </ContentWrapper>
    </Wrapper>
  )
}

export default Quotes

const Wrapper = styled.section`
  background: ${colors.white};
  padding: 17.36vw 12.92vw;
  ${media.tablet} {
    height: 141.487vw;
    padding: 31.25vw 4.883vw 19.53vw 4.883vw;
  }
  ${media.mobile} {
    padding: 54.667vw 15px 37.33vw;
    height: 242.933vw;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  width: 75.9vw;
  height: 41.88vw;
  ${media.tablet} {
    width: 100%;

    height: 100%;
    display: flex;
    flex-direction: column-reverse;
  }
  ${media.mobile} {
    width: 100%;
    height: 93%;
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 100;

  ${media.tablet} {
    position: relative;
  }
  ${media.mobile} {
    top: auto;
    bottom: 0;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
  }
`

const QuoteLine = styled.p<{ order: number; reverseOrder: number }>`
  transform: translate(-50px, 100px);
  transform-origin: 0% 0%;
  opacity: 0;
  white-space: pre;
`

const QuoteText = styled.div`
  ${text.desktop.h4};
  color: ${colors.blue400};
  margin-bottom: 2.78vw;
  position: relative;
  display: inline-block;

  ${media.tablet} {
    ${text.tablet.h4};
    margin-bottom: 7.81vw;
  }
  ${media.mobile} {
    ${text.mobile.h5};
    font-size: 8vw;
    height: 96vw;
    font-style: normal;
    font-weight: normal;
    font-size: 8vw;
    line-height: 120%;
    margin-bottom: 6.667vw;
  }
`

const Job = styled.div`
  ${text.desktop.d5};
  color: ${colors.black400};
  position: relative;

  height: 1.46vw;
  overflow: hidden;
  p {
    position: absolute;
    transform: translateY(110%);
  }

  ${media.tablet} {
    ${text.tablet.d4};
    line-height: 130%;
    height: 6vw;
  }
  ${media.mobile} {
    ${text.mobile.d5};
    height: 4.8vw;
  }
`

const Name = styled.div`
  ${text.desktop.bodyL};
  color: ${colors.black800};
  position: relative;

  height: 1.736vw;
  overflow: hidden;

  p {
    position: absolute;

    transform: translateY(110%);
  }
  ${media.tablet} {
    ${text.tablet.h5};
    height: 3.5vw;
  }
  ${media.mobile} {
    ${text.mobile.bodyL};
    height: 6vw;
  }
`

const Quote = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  padding-top: 8.33vw;
  ${media.tablet} {
    padding-top: 0;
  }
  ${media.mobile} {
    padding-top: 0;
  }
`

const Counter = styled.div`
  ${text.desktop.d4};
  ${strokeText};
  width: 7.64vw;
  margin-right: 1.88vw;
  display: flex;
  margin-top: 0.486vw;
  ${media.tablet} {
    ${text.tablet.d4}
    width: 9.961vw;
    margin-right: 3.418vw;
    margin-top: 0.486vw;
  }
  ${media.mobile} {
    width: 13.16vh;
    margin-bottom: 0;
    margin-right: 0;
    ${text.mobile.d4};
    margin-top: 0.486vw;
  }
`

const Ticker = styled.div`
  ${text.desktop.d4};
  ${strokeText};
  width: 1.3vw;
  overflow: hidden;
  position: relative;

  p {
    position: absolute;
    transform: translateY(110%);
  }
  ${media.tablet} {
    ${text.tablet.d4};
    width: 1.953vw;
  }
  ${media.mobile} {
    ${text.mobile.d4};
    width: 6vw;
  }
`

const BigQuotes = styled(BigQuotesSVG)`
  width: 18.958vw;
  height: 15.069vw;
  position: absolute;
  right: 11.181vw;
  top: 17.361vw;
  z-index: 2;
  path {
    fill: ${colors.black800};
  }
  ${media.tablet} {
    width: 26.66vw;
    height: 21.19vw;
    right: 4.883vw;
    top: 19.531vw;
  }
  ${media.mobile} {
    width: 37.333vw;
    height: 29.333vw;
    top: 40vw;
    right: 6.667vw;
  }
`

const MobileWrapper = styled.div`
  display: flex;

  ${media.mobile} {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }
`
